import React, {useEffect, useRef} from 'react';
import { ISearchRequest } from '../../../models/Search';
import { ICollectionResponse } from '../../../models/ICollectionResponse';
import { IConfiguratorProductSearchView } from '../../../models/Configurator';
import { useLazyPostSearchConfigurationProductsQuery } from '../../../store/Api';
import {PrimaryButton, Image, IconButton, TextField, Icon} from '@fluentui/react';
import ConfiguratorPanel from '../Product/ConfiguratorPanel';
import CurrencyDisplay from '../../Currency/CurrencyDisplay';
import ImageWithFallback from "../../Common/ImageWithFallback";

interface IConfiguratorSearchTableProps {
    searchCriteria: ISearchRequest;
    showResults?: boolean;
    onLoading?: (loading: boolean) => void;
    onResultsCountChange?: (count: number) => void;
    onHide?: () => void;
}

const ConfiguratorSearchTable: React.FC<IConfiguratorSearchTableProps> = (props) => {
    const [searchResults, setSearchResults] = React.useState<ICollectionResponse<IConfiguratorProductSearchView> | undefined>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedConfig, setSelectedConfig] = React.useState<IConfiguratorProductSearchView | undefined>();
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [leftHover, setLeftHover] = React.useState(false);
    const [rightHover, setRightHover] = React.useState(false);

    const [search] = useLazyPostSearchConfigurationProductsQuery();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const cardWidth = 200; // minWidth of card
    const gap = 54; // gap between cards

    const checkScroll = () => {
        const el = scrollContainerRef.current;
        if (el) {
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollLeft + el.clientWidth + 1 < el.scrollWidth);
        }
    };

    const handleScroll = () => checkScroll();


    const scrollByCard = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = cardWidth + gap;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };
    

    const runSearch = () => {
        setLoading(true);
        props.onLoading?.(true);
        search(props.searchCriteria)
            .unwrap()
            .then((response: ICollectionResponse<IConfiguratorProductSearchView>) => {
                setSearchResults(response);
                setLoading(false);
                props.onLoading?.(false);
                props.onResultsCountChange?.(response.items.length);
                checkScroll();
            })
            .catch((err: any) => {
                setError(err.message);
                setLoading(false);
                props.onLoading?.(false);
                props.onResultsCountChange?.(0);
            }
        );
    };

    React.useEffect(() => {
        runSearch();
    }, [props.searchCriteria]);

    useEffect(() => {
        const handleResize = () => checkScroll();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div style={{ display: props.showResults? undefined : 'none' }}>
            {searchResults && (
                <div style={{
                    position: 'relative',
                    background: '#f5f6fa',
                    borderRadius: 8,
                    padding: 24,
                    marginBottom: 24
                }}>
                    <div style={{display: 'flex'}} >
                        <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            ariaLabel="Close configurations"
                            title="Close"
                            style={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                background: 'transparent'
                            }}
                            onClick={() => props.onHide?.()}
                        />
                        <IconButton
                            iconProps={{ iconName: 'Refresh' }}
                            ariaLabel="Refresh configurations"
                            title="Refresh"
                            disabled={loading}
                            style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                background: 'transparent'
                            }}
                            onClick={runSearch}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginLeft: 40, marginBottom: 16 }}>
                        <h3 style={{ margin: 0 }}><Icon iconName='ProductVariant' styles={{root: {fontSize: 16, marginRight: 8, color: '#789c2f', verticalAlign: 'text-bottom'}}} aria-hidden />Configurations</h3>
                    </div>

                    <div style={{position: 'relative', width: '100%' }}>
                        {/* Left Arrow */}
                        {canScrollLeft &&
                            <IconButton
                                iconProps={{ iconName: 'ChevronLeft' }}
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 50,
                                    height: 280,
                                    zIndex: 1,
                                    background: leftHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                                }}
                                onClick={() => scrollByCard('left')}
                                ariaLabel="Scroll left"
                                onMouseEnter={() => setLeftHover(true)}
                                onMouseLeave={() => setLeftHover(false)}
                            />
                        }
                        {/* Scrollable Container */}

                        <div
                            ref={scrollContainerRef}
                            onScroll={handleScroll}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                height: '300px',
                                justifyContent: 'flex-start',
                                overflowX: 'auto',
                                width: '100%',
                                gap: '10px',
                                scrollBehavior: 'smooth'
                            }}
                        >
                    {searchResults.items.map((item) => (
                        <div key={item.id}
                             style={{
                                 borderRadius: '5px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ccc',
                                    padding: '10px',
                                    margin: '10px',
                                    cursor: 'pointer',
                                    minWidth: '200px',
                                    maxWidth: '200px'
                        }}>
                            <div style={{
                                height: '150px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            <ImageWithFallback src={item.image} alt={item.name}
                                               style={{width: '80%', height: 'auto', maxHeight: '100%', objectFit: 'contain', margin: '0 auto'}}/>
                            </div>
                            <div style={{height: '40px', width: '90%', display: 'flex', alignItems: 'center',}}>
                                <h3 style={{textAlign: 'center'}}>{item.name}</h3>

                            </div>
                            <PrimaryButton
                                iconProps={{iconName: 'edit'}}
                                text="Configure"
                                style={{ width: '100%' }}
                                onClick={() => {setSelectedConfig(item);}}
                            />
                            {item.baseCost && <p style={{fontStyle:'italic', textAlign: 'center'}}>Starting at <CurrencyDisplay value={item.baseCost} /></p>}
                        </div>
                    ))}
                        {/* Right Arrow */}
                        {canScrollRight &&
                            <IconButton
                                iconProps={{ iconName: 'ChevronRight' }}
                                style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 50,
                                    height: 280,
                                    zIndex: 1,
                                    background: rightHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                                }}
                                onClick={() => scrollByCard('right')}
                                ariaLabel="Scroll right"
                                onMouseEnter={() => setRightHover(true)}
                                onMouseLeave={() => setRightHover(false)}
                            />}
                        </div>
                    </div>
                </div>
            )}
            {selectedConfig && (
                <ConfiguratorPanel
                    configId={selectedConfig.id}
                    name={selectedConfig.name}
                    onClose={() => setSelectedConfig(undefined)}
                />
            )}
        </div>
    );
}

export default ConfiguratorSearchTable;